import React, { useState, useRef, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { X, Upload, RotateCw, ZoomIn, ZoomOut, Check } from 'lucide-react';
import { API_URL } from '../../utils/apiUrl';
import './ProfilePhotoModal.css';



export const ProfilePhotoModal = ({ isOpen, onClose, onSave, currentPhotoUrl, onRemove }) => {
  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const fileInputRef = useRef(null);

  // Load current photo when modal opens
  React.useEffect(() => {
    if (isOpen && currentPhotoUrl && !image) {
      // Load the current photo into the editor
      setImage(`${API_URL}${currentPhotoUrl}`);
    }
  }, [isOpen, currentPhotoUrl, image]);

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result);
      // Reset controls when new image is loaded
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
    };
    reader.readAsDataURL(file);
  };

  // Called when crop area changes
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Create cropped image
  const createCroppedImage = async () => {
    if (!image || !croppedAreaPixels) return null;

    return new Promise((resolve, reject) => {
      const imageElement = new Image();
      imageElement.src = image;
      
      imageElement.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set canvas size to desired output size (400x400)
        const outputSize = 400;
        canvas.width = outputSize;
        canvas.height = outputSize;

        // Fill with white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // The croppedAreaPixels gives us the exact pixels to extract from the source image
        // We simply need to draw that exact area onto our output canvas
        
        // Draw the cropped portion of the image directly
        // Source: croppedAreaPixels (x, y, width, height from original image)
        // Destination: full canvas (0, 0, outputSize, outputSize)
        ctx.drawImage(
          imageElement,
          croppedAreaPixels.x,           // Source x
          croppedAreaPixels.y,           // Source y
          croppedAreaPixels.width,       // Source width
          croppedAreaPixels.height,      // Source height
          0,                             // Destination x
          0,                             // Destination y
          outputSize,                    // Destination width
          outputSize                     // Destination height
        );

        // Convert to base64
        const croppedImage = canvas.toDataURL('image/jpeg', 0.95);
        resolve(croppedImage);
      };

      imageElement.onerror = reject;
    });
  };

  // Handle save
  const handleSave = async () => {
    try {
      const croppedImage = await createCroppedImage();
      if (croppedImage) {
        await onSave(croppedImage);
        handleClose();
      }
    } catch (error) {
      console.error('Error saving photo:', error);
      alert('Failed to save photo. Please try again.');
    }
  };

  // Handle close
  const handleClose = () => {
    setImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    onClose();
  };

  // Handle remove photo
  const handleRemove = async () => {
    if (onRemove) {
      await onRemove();
      handleClose();
    }
  };

  // Reset to default view
  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="profile-photo-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h3>Edit Profile Photo</h3>
          <button onClick={handleClose} className="modal-close" aria-label="Close">
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          {!image ? (
            // Upload State
            <div className="upload-area" onClick={() => fileInputRef.current?.click()}>
              <Upload size={48} />
              <p className="upload-title">Upload Photo</p>
              <p className="upload-subtitle">Click to select or drag and drop</p>
              <p className="upload-hint">JPG, PNG or WEBP (max 10MB)</p>
            </div>
          ) : (
            // Editing State
            <div className="editing-container">
              {/* Crop Area */}
              <div className="crop-container">
                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  rotation={rotation}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                  style={{
                    containerStyle: {
                      background: '#f3f4f6',
                    },
                  }}
                />
              </div>

              {/* Controls */}
              <div className="editing-controls-modern">
                {/* Zoom Control */}
                <div className="control-group-modern">
                  <label className="control-label">
                    <ZoomIn size={16} />
                    <span>Zoom</span>
                  </label>
                  <div className="slider-container">
                    <button
                      onClick={() => setZoom(Math.max(1, zoom - 0.1))}
                      className="slider-btn"
                      aria-label="Zoom out"
                    >
                      <ZoomOut size={16} />
                    </button>
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.01"
                      value={zoom}
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                      className="slider-input"
                    />
                    <button
                      onClick={() => setZoom(Math.min(3, zoom + 0.1))}
                      className="slider-btn"
                      aria-label="Zoom in"
                    >
                      <ZoomIn size={16} />
                    </button>
                  </div>
                  <span className="control-value">{zoom.toFixed(1)}x</span>
                </div>

                {/* Rotation Control */}
                <div className="control-group-modern">
                  <label className="control-label">
                    <RotateCw size={16} />
                    <span>Rotate</span>
                  </label>
                  <div className="rotation-buttons">
                    <button
                      onClick={() => setRotation((rotation - 90 + 360) % 360)}
                      className="control-btn"
                      aria-label="Rotate left"
                    >
                      <RotateCw size={18} style={{ transform: 'scaleX(-1)' }} />
                      <span>Left</span>
                    </button>
                    <span className="rotation-value">{rotation}°</span>
                    <button
                      onClick={() => setRotation((rotation + 90) % 360)}
                      className="control-btn"
                      aria-label="Rotate right"
                    >
                      <RotateCw size={18} />
                      <span>Right</span>
                    </button>
                  </div>
                </div>

                {/* Reset Button */}
                <button onClick={handleReset} className="btn-reset">
                  Reset Position
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="footer-left-actions">
            {image && (
              <button onClick={() => fileInputRef.current?.click()} className="btn-secondary-modal">
                Choose Different Photo
              </button>
            )}
            {currentPhotoUrl && onRemove && (
              <button onClick={handleRemove} className="btn-remove-modal">
                Remove Photo
              </button>
            )}
          </div>
          <div className="footer-actions">
            <button onClick={handleClose} className="btn-outline-modal">
              Cancel
            </button>
            <button onClick={handleSave} className="btn-primary-modal" disabled={!image}>
              <Check size={18} />
              <span>Save Photo</span>
            </button>
          </div>
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
};
